<template>
    <div class="form-group form-group-floating-label relative">
        <input
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="input"
            :name="name"
            :tabindex="tabindex"
            :required="required"
            :autofocus="autofocus"
            placeholder=" "
            :autocomplete="autocomplete"
            @input="$emit('input', $event.target.value)"
        >
        <div
            v-if="canToggleVisibility"
            class="absolute pin-t pin-r mt-lg pt-xs mr-sm"
            @click="showPassword = ! showPassword"
        >
            <font-awesome-icon
                :icon="showPassword ? 'eye-slash' : 'eye'"
                class="cursor-pointer text-gray-500 hover:text-gray-700"
            />
        </div>
        <label
            v-if="label"
            :for="`input-${name}`"
            class="floating-label"
        >
            {{ label }}
            <span
                v-if="help"
                class="text-xs italic"
            >
                ({{ help }})
            </span>
        </label>

        <slot v-if="hasSlotContent" :password="password" />
        <div v-else-if="showPasswordStrength" class="h-sm mb-sm">
            <svg
                viewBox="0 0 123 2"
                class="strength-bar text-neutral-light block mt-sm w-full rounded-full"
                preserveAspectRatio="none"
            >
                <path d="M0 1 L30 1" :class="getStrengthClass(0)" />
                <path d="M31 1 L61 1" :class="getStrengthClass(1)" />
                <path d="M62 1 L92 1" :class="getStrengthClass(2)" />
                <path d="M93 1 L123 1" :class="getStrengthClass(3)" />
            </svg>
        </div>

        <span v-if="error" class="form-error">
            {{ error }}
        </span>
    </div>
</template>

<script>
    export default {
        props: {
            value: {
                required: false,
                type: String,
                default: '',
            },
            name: {
                required: true,
                type: String,
            },
            label: {
                required: false,
                type: String,
                default: '',
            },
            required: {
                required: false,
                type: Boolean,
                default: false,
            },
            tabindex: {
                required: false,
                type: Number,
                default: null,
            },
            strengthClasses: {
                type: Array,
                default() {
                    return [
                        'text-neutral-dark', // very weak
                        'text-danger', // weak
                        'text-warning', // medium
                        'text-success', // good
                        'text-success-darker', // great
                    ];
                },
            },
            autofocus: {
                required: false,
                type: Boolean,
                default: null,
            },
            autocomplete: {
                required: false,
                type: String,
                default: 'off',
            },
            help: {
                type: String,
                default: null,
            },
            canToggleVisibility: {
                type: Boolean,
                default: false,
            },
            showPasswordStrength: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                password: this.value,
                showPassword: false,
                error: null,
            };
        },
        computed: {
            hasSlotContent() {
                return !! this.$scopedSlots.default;
            },
            strength() {
                if (this.password && this.password.length < 1) {
                    return 0;
                }

                return zxcvbn(this.password || '').score;
            },
            strengthClass() {
                if (this.strength >= 0) {
                    if (this.strengthClasses[this.strength]) {
                        return this.strengthClasses[this.strength];
                    }
                }

                return this.strengthClasses[0];
            },
        },
        watch: {
            value(newInput) {
                this.password = newInput;
            },
        },
        created() {
            if (window.App && window.App.errors && window.App.errors.hasOwnProperty(this.name)) {
                this.error = window.App.errors[this.name][0];
            }
        },
        methods: {
            getStrengthClass(strength) {
                if (this.strength > strength) {
                    return this.strengthClass;
                }

                return '';
            },
        },
    };
</script>

<style scoped>
    .strength-bar path {
        stroke: currentColor;
        stroke-width: 2;
        transition: width 0.5s ease-in-out, color 0.25s;
    }
</style>
